/**
 * Statusanzeige „Schmied arbeitet: …“ — gemeinsame Regeln. (16.10.2026)
 *
 * Der Entwickler („Schmied“) setzt am Server einen kurzen Status (scripts/schmied-status.mjs); Martins App zeigt ihn als
 * Warteblase am Ende des Verlaufs (frontend/lib/schmied-warteblase.ts). Er ist KEIN Chat-Beitrag: nicht in chat_messages, nie
 * gespeichert, nie im Prompt von Vespera.
 */

import type { ZusatzId } from "./entwickler.js";

/** Name des SSE-Ereignisses auf dem Chat-Strom. */
export const SCHMIED_STATUS_EREIGNIS = "schmied-status";
export const SCHMIED_STATUS_TEXT_HOECHSTENS = 80;
/** Ohne Erneuerung verfällt der Status nach so langer Zeit. */
export const SCHMIED_STATUS_VERFALL_MS = 30 * 60_000;

/*
 * „Zimmermann arbeitet“ (17.10.2026, Martin 15:08): dieselbe Warteblase wie beim Schmied, mit SEINEM Namen — aber ein eigener,
 * unabhängiger Stand (server/schmied-status.ts `getZimmermannStatusKanal`), eigenes Ereignis, nie ein Chat-Beitrag. Gesetzt wird er
 * allein vom Server, wenn ein Beitrag des Zimmermanns `--arbeitet` (Feld `weiter`) trägt; jeder spätere Beitrag ohne das Feld
 * (oder der Verfall) nimmt ihn weg. Der Text ist FEST — nichts vom Zimmermann gelangt in die Anzeige. Er hält nie ein Mikro.
 */
export const ZIMMERMANN_STATUS_EREIGNIS = "zimmermann-status";
/** 17.10.2026 (Martin 19:46): „Hüter arbeitet / hat deine Nachricht“ — dieselben Texte, eigener Stand, eigenes Ereignis. */
export const HUETER_STATUS_EREIGNIS = "hueter-status";
/** Das SSE-Ereignis je Zusatz-Entwickler (shared/entwickler.ts `ZUSATZ_IDS`). */
export const ZUSATZ_STATUS_EREIGNIS: Readonly<Record<ZusatzId, string>> = { zimmermann: ZIMMERMANN_STATUS_EREIGNIS, hueter: HUETER_STATUS_EREIGNIS };
export const ZIMMERMANN_STATUS_TEXT = "arbeitet gerade";
/**
 * Geht eine Nachricht Martins an den Zimmermann, zeigt der Server SOFORT eine der beiden festen Zeilen — je nachdem, ob ein Hörer des
 * Zimmermanns verbunden ist (nur diese Tatsache, nie ein Inhalt). Martin weiß so gleich, ob eine Antwort kommen kann.
 */
export const ZIMMERMANN_STATUS_HAT_TEXT = "hat deine Nachricht";
export const ZIMMERMANN_STATUS_NICHT_TEXT = "gerade nicht verbunden";
/** Die Weiche-KI fiel aus oder war unsicher: die Nachricht blieb bei Vespera (sichere Seite) — gleiche feste Zeile, anderer Grund. */
export const ENTWICKLER_STATUS_AUSFALL_TEXT = "hat das nicht bekommen – Weiche unsicher, Vespera antwortet";
/** Diese Zeile verschwindet nach spätestens 8 Sekunden von selbst (Martin 22.10.2026) — nicht erst nach ZIMMERMANN_STATUS_VERFALL_MS. */
export const ENTWICKLER_STATUS_AUSFALL_VERFALL_MS = 8_000;
/**
 * Quittung für eine reine Zustimmung („Ja“) an den Schmied, wenn keine Antwort erwartet wird (Martin 27.10.2026: „kommt nichts zurück“).
 * Feste Texte, kein Inhalt; der Name „Schmied“ steht schon in der Blase. Kurz (8 s), ruhig, öffnet kein Mikro.
 */
export const SCHMIED_STATUS_ANGEKOMMEN_TEXT = "Angekommen · hört mit";
export const SCHMIED_STATUS_ANGEKOMMEN_NICHT_TEXT = "Angekommen · hört gerade nicht mit";
/** Kurz nach einem Neustart des Servers: der Hörer verbindet sich gerade neu (Nachrichten kommen per „seit“ nach). */
export const SCHMIED_STATUS_ANGEKOMMEN_NEU_TEXT = "Angekommen · verbindet sich neu";
/** Hört er mit: „Angekommen · gerade keine Helfer“ bzw. „Angekommen · arbeitet an: 2 bauen, 1 prüfen“ (feste Kurzliste, ohne Details). */
export const SCHMIED_STATUS_ANGEKOMMEN_KEINE_HELFER_TEXT = "Angekommen · gerade keine Helfer";
export function angekommenHelferText(helfer: string): string {
  return helfer ? `Angekommen · arbeitet an: ${helfer}` : SCHMIED_STATUS_ANGEKOMMEN_KEINE_HELFER_TEXT;
}
/** Ist das eine der kurzen „Angekommen“-Zeilen (8 s, hält nie das Mikro)? */
export function istAngekommenText(t: string | null | undefined): boolean {
  return typeof t === "string" && (t === SCHMIED_STATUS_ANGEKOMMEN_TEXT || t.startsWith("Angekommen · "));
}
export const SCHMIED_STATUS_ANGEKOMMEN_VERFALL_MS = ENTWICKLER_STATUS_AUSFALL_VERFALL_MS;
/** Höchstens eine solche Quittung je Konto in dieser Zeit (Spam-Schutz). */
export const SCHMIED_STATUS_ANGEKOMMEN_SPERRE_MS = 20_000;
/** Kürzer als beim Schmied: eine vergessene Zeile hängt höchstens so lange. */
export const ZIMMERMANN_STATUS_VERFALL_MS = 10 * 60_000;

/**
 * Automatischer Status: geht eine Nachricht Martins an den Schmied, setzt der Server diesen Text selbst (server/dreier-modus.ts);
 * er verschwindet mit dem Beitrag des Schmieds (server/dreier-routen.ts) oder nach SCHMIED_STATUS_AUTO_VERFALL_MS.
 */
export const SCHMIED_STATUS_AUTO_TEXT = "bereitet eine Antwort vor";
export const SCHMIED_STATUS_AUTO_VERFALL_MS = 6 * 60_000;

/**
 * ÜBERGABE (18.10.2026, Martin): übergibt Vespera ausdrücklich an den Schmied („Schmied, … du bist dran“), zeigt der Server SOFORT eine
 * der beiden FESTEN Zeilen — je nachdem, ob ein Hörer des Schmieds verbunden ist (nur diese Tatsache, nie ein Wortlaut der Übergabe).
 * Sie steht höchstens SCHMIED_STATUS_UEBERGABE_VERFALL_MS und geht mit dem ersten Beitrag des Schmieds.
 */
export const SCHMIED_STATUS_UEBERGABE_TEXT = "übernimmt …";
export const SCHMIED_STATUS_UEBERGABE_NICHT_TEXT = "hört gerade nicht zu";
export const SCHMIED_STATUS_UEBERGABE_VERFALL_MS = 90_000;

/** Takt, in dem die Sofort-Quittung ihre Sekunden erneuert (ersetzt die Zeile, hängt nichts an). */
export const SCHMIED_QUITTUNG_TAKT_MS = 15_000;
/**
 * Die Zeile muss bei 390 px in EINER Zeile stehen (Martin 17.10., 09:49): höchstens so viele Zeichen, Sekunden inklusive.
 * Der Name „Schmied“ steht schon in der Blase und gehört nicht dazu.
 */
export const SCHMIED_QUITTUNG_HOECHSTENS = 26;
/** Stichwort der Arbeit: höchstens so lang (und nur so lang, wie die Zeile es zulässt). */
export const SCHMIED_QUITTUNG_STICHWORT_HOECHSTENS = 14;

/** Zusatz-Entwickler (Zimmermann/Hüter): „hat deine Nachricht · 15 s“ bzw. „gerade nicht verbunden · 15 s“ — nur der Hörer-Stand, nie „arbeitet“. */
export function baueZusatzQuittungText(hoert: boolean, seitMs: number): string {
  return `${hoert ? ZIMMERMANN_STATUS_HAT_TEXT : ZIMMERMANN_STATUS_NICHT_TEXT}${seitText(seitMs)}`;
}

/** Der Text ohne den Zeitzähler „ · 15 s“ / „ · 1 Min“ am Ende — damit feste Texte auch mit Zähler erkannt werden. */
export function ohneZeitzaehler(t: string): string {
  return t.replace(/ · \d+ (?:s|Min)$/, "");
}

/** Was der Server über den Schmied wirklich weiß — mehr nicht. */
export interface SchmiedLage {
  /** Ein Mithör-Strom mit frischem Lebenszeichen ist offen (server/dreier-modus.ts `schmiedHoertMit`). */
  hoert: boolean;
  /** Stichwort der laufenden Arbeit (Marke „Entwicklung“, server/schmied-arbeit.ts), sonst null. */
  arbeit: string | null;
  /** Feste Kurzliste der laufenden Helfer („2 bauen, 1 prüfen“, shared/schmied-arbeit.ts `helferKurzliste`); „“ = keine. Fehlt das Feld, gilt `arbeit`. */
  helfer?: string;
}

/** „· 15 s“ / „· 1 Min“ — ab der ersten Takt-Marke, davor leer. */
function seitText(seitMs: number): string {
  const s = Math.floor(seitMs / SCHMIED_QUITTUNG_TAKT_MS) * (SCHMIED_QUITTUNG_TAKT_MS / 1000);
  if (s <= 0) return "";
  return s < 60 ? ` · ${s} s` : ` · ${Math.floor(s / 60)} Min`;
}

/**
 * Die ehrliche Sofort-Quittung (ohne Modell): nur Zustände, die der Server kennt. Der Name „Schmied“ steht schon in der Blase.
 * Höchstens SCHMIED_QUITTUNG_HOECHSTENS Zeichen: „nicht verbunden · 30 s“, „in der Reihe · 15 s“, „arbeitet: Tor · 45 s“.
 */
export function baueQuittungText(lage: SchmiedLage, seitMs: number): string {
  const seit = seitText(seitMs);
  if (!lage.hoert) return `nicht verbunden${seit}`;
  if (lage.helfer !== undefined) return lage.helfer ? `arbeitet an: ${lage.helfer}${seit}` : `in der Reihe${seit}`;
  const arbeit = lage.arbeit ? ordneStatusText(lage.arbeit) : "";
  if (!arbeit) return `in der Reihe${seit}`;
  const kopf = "arbeitet: ";
  const platz = Math.min(SCHMIED_QUITTUNG_STICHWORT_HOECHSTENS, SCHMIED_QUITTUNG_HOECHSTENS - kopf.length - seit.length);
  const wort = arbeit.length > platz ? `${arbeit.slice(0, Math.max(platz - 1, 1)).trimEnd()}…` : arbeit;
  return `${kopf}${wort}${seit}`;
}

/** Der Stand, wie er über den Strom geht: `text` null = nichts anzeigen; `restMs` = Restdauer bis zum Verfall. */
export interface SchmiedStatusStand {
  text: string | null;
  restMs: number;
  /**
   * Nur bei `SCHMIED_STATUS_HAELT_MIKRO=0` am Server: die Anzeige hält das wartende Mikro NICHT über die Frist hinaus zu
   * (shared/schmied.ts, frontend/lib/schmied-warten.ts) — altes Verhalten. Fehlt das Feld, hält sie.
   */
  mikroFrei?: true;
}

/** Leerraum glätten, Steuerzeichen raus. */
export function ordneStatusText(t: string): string {
  // eslint-disable-next-line no-control-regex
  return t.replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim();
}

/** Der Stand aus einem SSE-Frame — nur die erwartete Form, sonst null. */
export function leseSchmiedStatus(roh: unknown): SchmiedStatusStand | null {
  if (!roh || typeof roh !== "object") return null;
  const { text, restMs, mikroFrei } = roh as { text?: unknown; restMs?: unknown; mikroFrei?: unknown };
  if (text === null) return { text: null, restMs: 0 };
  if (typeof text !== "string") return null;
  const t = ordneStatusText(text);
  if (!t || t.length > SCHMIED_STATUS_TEXT_HOECHSTENS) return null;
  const rest =
    typeof restMs === "number" && Number.isFinite(restMs)
      ? Math.min(Math.max(restMs, 0), SCHMIED_STATUS_VERFALL_MS)
      : SCHMIED_STATUS_VERFALL_MS;
  return mikroFrei === true ? { text: t, restMs: rest, mikroFrei: true } : { text: t, restMs: rest };
}

/*
 * STUFEN DES WARTENS (19.10.2026, Martin 08:44: „hat deine Nachricht“ stand ewig, ohne dass je eine Antwort kam). EINE Stelle für alle
 * Entwickler (Schmied, Zimmermann, Hüter): eine Zeile, die nur „zugestellt / bereitet vor / übernimmt“ sagt, wird mit der Zeit ehrlicher.
 * „gerade nicht verbunden“ (ZIMMERMANN_STATUS_NICHT_TEXT) sagt schon alles und wird nie umgeschrieben. Rein, ohne Uhr.
 */
export const WARTE_SPAET_MS = 60_000;
export const WARTE_STILL_MS = 5 * 60_000;
export const WARTE_SPAET_TEXT = "hat noch nicht geantwortet";
export const WARTE_STILL_TEXT = "antwortet nicht – ist er noch verbunden?";
/** 26.10.2026 (Martin): wer nach so langer Zeit noch schweigt, gilt als abgelaufen — ausgegraut, nicht mehr „laufend“. Nur „zugestellt“-Zeilen, nie „arbeitet“. */
export const WARTE_ABGELAUFEN_MS = 8 * 60_000;
export const WARTE_ABGELAUFEN_TEXT = "keine Antwort (abgelaufen)";
/** Die feste Hinweiszeile, die „Nochmal anstoßen“ an den Entwickler schickt (nie Martins Wortlaut). */
export const ANSTOSS_ZEILE = "Martin wartet auf Antwort";
/** Höchstens ein Anstoß je Entwickler und Minute (Server und App). */
export const ANSTOSS_SPERRE_MS = 60_000;
export type WarteStufe = "ok" | "spaet" | "still" | "abgelaufen";

const ZUGESTELLT_TEXTE: readonly string[] = [ZIMMERMANN_STATUS_HAT_TEXT, SCHMIED_STATUS_AUTO_TEXT, SCHMIED_STATUS_UEBERGABE_TEXT];

/** Die Stufe einer Statuszeile nach `seitMs` Warten; nur „zugestellt“-Zeilen steigen auf. */
export function warteStufe(text: string, seitMs: number): WarteStufe {
  if (!ZUGESTELLT_TEXTE.includes(ohneZeitzaehler(text))) return "ok"; // auch „hat deine Nachricht · 90 s“
  if (seitMs >= WARTE_ABGELAUFEN_MS) return "abgelaufen";
  return seitMs >= WARTE_STILL_MS ? "still" : seitMs >= WARTE_SPAET_MS ? "spaet" : "ok";
}

/** Der angezeigte Text zur Stufe. */
export function stufenText(text: string, stufe: WarteStufe): string {
  return stufe === "abgelaufen" ? WARTE_ABGELAUFEN_TEXT : stufe === "still" ? WARTE_STILL_TEXT : stufe === "spaet" ? WARTE_SPAET_TEXT : text;
}
